import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Feature, getFeatureByAddOn, PlanInterval, SubscriptionSource } from '../models';
import { fromOrder, fromSubscription, useAppDispatch } from '../store';
import { useNavigate, useQuery } from '../routes';
import { plan } from '../layout/routes';

export const usePreviewTrue = () => {
  const dispatch = useAppDispatch();
  const { navigate, replaceQuery } = useNavigate();
  const { queryDictionary } = useQuery();
  const subscription = useSelector(
    fromSubscription.selectTrueSubscriptions(SubscriptionSource.RECURLY),
  );

  const prevRequest = useSelector(fromOrder.selectPreviousOrderPreviewRequest);
  const loading = useSelector(fromOrder.selectPreviewLoading);
  const truePlatformPreviewRequest = useSelector(fromOrder.selectTruePlatformPreviewRequest);
  const addOns = useSelector(fromOrder.selectAddOns);
  const dictionary = queryDictionary();

  useEffect(() => {
    const updateDictionary = (
      name: string,
      value?: any,
    ) => {
      if (!value) {
        delete dictionary[name];
      } else {
        dictionary[name] = value;
      }
    };

    const updateQueryParams = (
      addOn: Record<string, number> | undefined,
      quantity?: number,
      interval?: PlanInterval,
    ) => {
      // update the query params to reflect the current preview request,
      // so when user share the URL, it will reflect the previewed subscription
      updateDictionary('trueTeamQuota', quantity);
      updateDictionary('trueTeamCycle', interval);
      if (addOn) {
        Object.entries(addOn).forEach(([addOn, session]) => {
          const addOnFeature = getFeatureByAddOn(addOn.toLowerCase());
          const prefix = Feature.getFeaturePrefix(addOnFeature);
          updateDictionary(`${prefix}Quota`, session > 0 ? session : undefined);
        });
      }

      navigate(plan.path, replaceQuery(dictionary));
    };

    const shouldPreview = (filteredAddOn: Record<string, number> | undefined) => {
      if (loading || !truePlatformPreviewRequest) {
        return false;
      }

      if (prevRequest && (prevRequest.quantity !== truePlatformPreviewRequest.quantity
        || prevRequest.productCode !== truePlatformPreviewRequest.productCode
        || Object.entries(prevRequest.addOns ?? {}).some(
          ([addOn, session]) => filteredAddOn?.[addOn] !== session,
        )
      )) {
        return true;
      }

      if (!subscription) {
        return truePlatformPreviewRequest.quantity > 0;
      }

      if (truePlatformPreviewRequest.quantity === 0) {
        return filteredAddOn && Object.entries(filteredAddOn).length > 0;
      }

      return true;
    };

    // do not preview if truePlatformPreviewRequest is not ready, or if it's loading
    if (!truePlatformPreviewRequest) {
      return;
    }

    const filteredAddOn = addOns
      ? Object.fromEntries(Object.entries(addOns)
        .filter(([, session]) => session > 0))
      : {};
    if (truePlatformPreviewRequest?.quantity === 0
      && (addOns === undefined || Object.entries(filteredAddOn).length === 0)) {
      dispatch(fromOrder.resetOrderPreviewResponse());
      updateQueryParams(addOns);
      return;
    }

    if (truePlatformPreviewRequest && shouldPreview(filteredAddOn)) {
      const truePreviewRequest = {
        ...truePlatformPreviewRequest,
        addOns: filteredAddOn,
      };
      dispatch(fromOrder.setPrevTruePlatformPreviewRequest(truePreviewRequest));
      dispatch(fromOrder.doPreviewOrder({
        singleProductList: [truePreviewRequest],
      }))
        .finally(() => {
          updateQueryParams(
            addOns,
            truePreviewRequest.quantity,
            PlanInterval.getIntervalFromPlanCode(truePreviewRequest.productCode),
          );
        });
    }
  }, [truePlatformPreviewRequest, addOns]);
};
